import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  useAlert,
} from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DeleteSerieModal,
  SERIE_PAGE_SIZE,
  SerieSearchBar,
  SerieTable,
  useDeleteSerie,
  useSerieFilters,
  useSerieList,
} from '@/features/series-management';
import type { SerieGridItemViewModel } from '@/shared/api/generated';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/series/')({
  component: SerieListPage,
});

/** Página de listagem de Séries. */
export function SerieListPage() {
  const { t } = useTranslation('series');
  const { alert } = useAlert();
  const navigate = useNavigate();

  const { filters, updateFilters, resetFilters } = useSerieFilters();
  const { series, totalCount, totalPages, currentPage, isLoading, isError, refetch } =
    useSerieList(filters);
  const deleteSerie = useDeleteSerie();

  const [deleting, setDeleting] = useState<SerieGridItemViewModel | null>(null);

  const handleDeleteConfirm = () => {
    if (deleting?.id == null) return;
    deleteSerie.mutate(
      { id: deleting.id },
      {
        onSuccess: () => alert({ alertType: 'success', title: t('series.toast.deleteSuccess') }),
        onError: () => alert({ alertType: 'warning', title: t('series.toast.deleteError') }),
        onSettled: () => setDeleting(null),
      },
    );
  };

  const addButton = (
    <Button
      hierarchy="primary"
      size="lg"
      leadingIcon={PlusIcon}
      onClick={() => void navigate({ to: '/series/create' })}
    >
      {t('series.actions.add')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-mini">
      <PageHeader
        title={t('series.title')}
        subtitle={t('series.subtitle', { count: totalCount })}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('series.breadcrumb.series')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={addButton}
      />

      {/* TODO series: state de erro */}
      {isError ? (
        <div className="flex flex-col items-start gap-nano rounded-md border-hairline border-base-danger-300 bg-ctx-layout-surface p-xxs">
          <p className="text-base-danger-700 ts-paragraph-xs">{t('series.toast.loadError')}</p>
          <Button hierarchy="secondary" size="md" onClick={() => void refetch()}>
            {t('series.loadErrorRetry')}
          </Button>
        </div>
      ) : (
        <>
          <SerieSearchBar
            defaultValue={filters.search}
            defaultFilter={filters.filter}
            defaultCompanyId={filters.companyId}
            onSearch={({ search, filter, companyId }) =>
              updateFilters({ search, filter, companyId })
            }
            onClear={resetFilters}
          />

          <SerieTable
            data={series}
            isLoading={isLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={SERIE_PAGE_SIZE}
            onPageChange={page => updateFilters({ page })}
            emptyStateAction={addButton}
            onEdit={serie =>
              void navigate({ to: '/series/$serieId', params: { serieId: String(serie.id) } })
            }
            onManageVideos={serie =>
              void navigate({
                to: '/series/$serieId/videos',
                params: { serieId: String(serie.id) },
              })
            }
            onManageLangs={serie =>
              void navigate({
                to: '/series/$serieId/langs',
                params: { serieId: String(serie.id) },
              })
            }
            onDelete={setDeleting}
          />
        </>
      )}

      <DeleteSerieModal
        open={deleting !== null}
        serieTitle={deleting?.langName ?? ''}
        onConfirm={handleDeleteConfirm}
        onOpenChange={open => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
}
